<template>
  <div id="email_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, key) in analyticsData"
        :key="key"
        source="super_agi_email_activity_analytics"
        :title="data.title"
        :description="range?.label || ''"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, reactive, watchEffect } from "vue";
import {
  formatAnalyticsValue,
  toFetchEmailCampaignCount,
} from "@/src/utils/analyticUtils.js";
import { emailCountActions, units } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);

const analyticsData = reactive({
  emails_sent: { title: "Emails sent", value: 0, isLoading: true },
  emails_replied: { title: "Emails replied", value: 0, isLoading: true },
  positive_replies: { title: "Positive replies", value: 0, isLoading: true },
  emails_bounced: {
    title: "Percentage of bounced emails",
    value: 0,
    isLoading: true,
  },
  reply_rate: {
    title: "Reply Rate",
    value: 0,
    isLoading: true,
    unit: units.RATE,
  },
  positive_reply_rate: {
    title: "Positive Reply Rate",
    value: 0,
    isLoading: true,
    unit: units.RATE,
  },
});

const getParams = () => ({
  email_id: filters.value?.mailbox?.id,
  start_time: range.value?.start,
  end_time: range.value?.end,
});

const updateAnalyticItem = (key, value) => {
  if (analyticsData[key]) {
    analyticsData[key].value = value ?? 0;
    analyticsData[key].isLoading = false;
  }
};

const fetchAnalytics = async () => {
  const params = getParams();
  Object.values(analyticsData).forEach((item) => (item.isLoading = true));

  toFetchEmailCampaignCount({ ...params, action: emailCountActions.EMAIL_SENT })
    .then((result) => updateAnalyticItem("emails_sent", result))
    .catch(() => updateAnalyticItem("emails_sent", 0));

  toFetchEmailCampaignCount({
    ...params,
    action: emailCountActions.EMAIL_REPLIED,
  })
    .then((result) => updateAnalyticItem("emails_replied", result))
    .catch(() => updateAnalyticItem("emails_replied", 0));

  toFetchEmailCampaignCount({
    ...params,
    action: emailCountActions.EMAIL_POSITIVE_REPLIED,
  })
    .then((result) => updateAnalyticItem("positive_replies", result))
    .catch(() => updateAnalyticItem("positive_replies", 0));

  toFetchEmailCampaignCount({
    ...params,
    action: emailCountActions.EMAIL_POSITIVE_REPLIED_RATE,
  })
    .then((result) => updateAnalyticItem("positive_reply_rate", result))
    .catch(() => updateAnalyticItem("positive_reply_rate", 0));

  toFetchEmailCampaignCount({
    ...params,
    action: emailCountActions.EMAIL_REPLIED_RATE,
  })
    .then((result) => updateAnalyticItem("reply_rate", result))
    .catch(() => updateAnalyticItem("reply_rate", 0));

  toFetchEmailCampaignCount({
    ...params,
    action: emailCountActions.EMAIL_BOUNCED,
  })
    .then((result) => updateAnalyticItem("emails_bounced", result))
    .catch(() => updateAnalyticItem("emails_bounced", 0));
};

watchEffect(() => {
  if (range.value) fetchAnalytics();
});

useAnalyticsRefresh(fetchAnalytics, [range]);
</script>
